import { FormApi } from 'final-form'
import { StudioSystemType } from 'types/global'
import { CheckboxGroupInput, DropdownInput, NativeMountingInputs, TextFieldInput } from 'types/mounting'
import { createDlubalCASnowSrButton, createDlubalCASnowSsButton, createDlubalCAWindButton } from '../../dlubalButtonConfigs'
import { RoofTypeName } from '../../types'
import { getRoofName } from '../../utils'
import { syncFastRackHiddenClampLandscapeWarning } from './FastRack'
import { getFastRackCalculatedSpanValue, getFastRackFacetDesignSnowLabels } from './FastRackCalculatedSpan'
import { ANCHOR_SKU, normalizeAnchorType } from './FastRackCatalogue'
import { MOUNT_FR } from './fastRackLabels'

export const FAST_RACK_DEFAULT_COLOUR = 'Black'
export const FAST_RACK_PREFERRED_RAILS_DEFAULT = ['94', '139', '185']

/** Ultrabuilder Terrain tooltip copy (translation key). */
export const FAST_RACK_TERRAIN_TOOLTIP = MOUNT_FR.terrainTooltip

export const FAST_RACK_PREFERRED_RAILS_TOOLTIP = MOUNT_FR.preferredRailsTooltip

type RoofGroup = 'asphaltShingle' | 'metalStandingSeam' | 'corrugatedMetal' | 'unknown'

function classifyRoof(roofType: RoofTypeName | undefined): RoofGroup {
  if (roofType === 'Composition / Asphalt Shingle') return 'asphaltShingle'
  if (roofType === 'Metal Standing Seam') return 'metalStandingSeam'
  if (roofType === 'Metal Tin') return 'corrugatedMetal'
  return 'unknown'
}

export function getLandscapeGridNames(system: StudioSystemType): string[] {
  const grids = system.moduleGrids?.() ?? []
  const names: string[] = []
  grids.forEach((mg: any, index: number) => {
    if (mg.moduleLayout() === 'landscape') {
      const facetName = mg.facet?.getName?.()
      names.push(facetName || `Grid ${index + 1}`)
    }
  })
  return names
}

export function createFastRackInputs(): (projectForm: FormApi, system: StudioSystemType) => NativeMountingInputs {
  return (projectForm: FormApi, system: StudioSystemType): NativeMountingInputs => {
    const hiddenClampValue = (system.integration_json?.fastRack?.hiddenClamp as string) || 'yes'
    const landscapeNames = getLandscapeGridNames(system)
    syncFastRackHiddenClampLandscapeWarning(system.uuid, hiddenClampValue, landscapeNames)

    const roofType = getRoofName(projectForm.getState().values.roof_type) as RoofTypeName | undefined
    const roofGroup = classifyRoof(roofType)
    const integrationData = (system.integration_json?.fastRack ?? {}) as Record<string, any>

    const dropdownInputs: DropdownInput[] = []

    // --- Anchor type (filtered by roof) ---
    if (roofGroup === 'asphaltShingle') {
      dropdownInputs.push({
        label: MOUNT_FR.anchorType,
        variableName: 'anchorType',
        default: ANCHOR_SKU.TALON_XE,
        options: [
          { label: MOUNT_FR.anchorTalonXe, value: ANCHOR_SKU.TALON_XE },
          { label: MOUNT_FR.anchorRtMini, value: ANCHOR_SKU.RT_MINI_BOX_20 },
        ],
      })
    } else if (roofGroup === 'metalStandingSeam') {
      dropdownInputs.push({
        label: MOUNT_FR.anchorType,
        variableName: 'anchorType',
        default: ANCHOR_SKU.S5_CLAMPS,
        options: [{ label: MOUNT_FR.anchorS5Clamps, value: ANCHOR_SKU.S5_CLAMPS }],
      })
    } else if (roofGroup === 'corrugatedMetal') {
      dropdownInputs.push({
        label: MOUNT_FR.anchorType,
        variableName: 'anchorType',
        default: ANCHOR_SKU.PM_BRACKET,
        options: [
          { label: MOUNT_FR.anchorPmBracket, value: ANCHOR_SKU.PM_BRACKET },
          { label: MOUNT_FR.anchorEjot, value: ANCHOR_SKU.EJOT_8_0_110 },
          { label: MOUNT_FR.anchorS5RibBracket, value: ANCHOR_SKU.S5_RIBBRACKET },
        ],
      })
    }

    const textFieldInputsAfterAnchor: TextFieldInput[] = []
    const currentAnchor = normalizeAnchorType((integrationData.anchorType as string) || '')
    if (currentAnchor === ANCHOR_SKU.RT_MINI_BOX_20) {
      textFieldInputsAfterAnchor.push({
        label: MOUNT_FR.rtMiniSpan,
        variableName: 'rtMiniCalcSpan',
        default: 48,
        type: 'number',
        min: 1,
        step: 1,
      })
    }
    if (roofGroup === 'metalStandingSeam') {
      textFieldInputsAfterAnchor.push({
        label: MOUNT_FR.seamSpacing,
        variableName: 'seamSpacing',
        default: 9,
        type: 'number',
        min: 1,
      })
    }
    if (roofGroup === 'corrugatedMetal') {
      textFieldInputsAfterAnchor.push({
        label: MOUNT_FR.majorRibSpacing,
        variableName: 'majorRibSpacing',
        default: 9,
        type: 'number',
        min: 1,
      })
    }

    // --- Mount type (Asphalt Shingle only) ---
    if (roofGroup === 'asphaltShingle') {
      dropdownInputs.push({
        label: MOUNT_FR.mountType,
        variableName: 'mountType',
        default: 'SHEATHING',
        options: [
          { label: MOUNT_FR.sheathing, value: 'SHEATHING' },
          { label: MOUNT_FR.truss, value: 'TRUSS' },
        ],
      })
    }

    // --- Terrain (Ultrabuilder label; stored as exposure for span tables) ---
    dropdownInputs.push({
      label: MOUNT_FR.terrain,
      variableName: 'exposure',
      default: 'Rough',
      tooltip: FAST_RACK_TERRAIN_TOOLTIP,
      options: [
        { label: MOUNT_FR.terrainRough, value: 'Rough' },
        { label: MOUNT_FR.terrainOpen, value: 'Open' },
      ],
    })

    // --- Hidden end clamps ---
    dropdownInputs.push({
      label: MOUNT_FR.hiddenEndClamps,
      variableName: 'hiddenClamp',
      default: 'yes',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ],
    })

    // --- End caps ---
    dropdownInputs.push({
      label: MOUNT_FR.includeEndCaps,
      variableName: 'endCaps',
      default: 'yes',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
      ],
    })

    // --- Clamp style ---
    dropdownInputs.push({
      label: MOUNT_FR.clampStyle,
      variableName: 'clampStyle',
      default: 'clickLock',
      options: [
        { label: MOUNT_FR.clampClickLock, value: 'clickLock' },
        { label: MOUNT_FR.clampTBolt, value: 'tBolt' },
      ],
    })

    // --- Component colour ---
    dropdownInputs.push({
      label: MOUNT_FR.clampColour,
      variableName: 'componentColour',
      default: FAST_RACK_DEFAULT_COLOUR,
      options: [
        { label: 'Black', value: 'Black' },
        { label: 'Silver', value: 'Silver' },
      ],
    })

    // --- Pest protection ---
    dropdownInputs.push({
      label: MOUNT_FR.pestProtection,
      variableName: 'pestProtection',
      default: 'none',
      options: [
        { label: MOUNT_FR.pestEdgeTrim, value: 'edgeTrim' },
        { label: MOUNT_FR.pestCritterGuard, value: 'critterGuard' },
        { label: 'None', value: 'none' },
      ],
    })

    // --- Fence sub-options (Critter Guard only) ---
    const currentPest = (integrationData.pestProtection as string) || 'none'
    if (currentPest === 'critterGuard') {
      dropdownInputs.push({
        label: MOUNT_FR.fenceHeight,
        variableName: 'fenceHeight',
        default: '6',
        options: [
          { label: MOUNT_FR.fenceHeight6, value: '6' },
          { label: MOUNT_FR.fenceHeight8, value: '8' },
        ],
      })

      dropdownInputs.push({
        label: MOUNT_FR.fenceAttachmentType,
        variableName: 'fenceAttachment',
        default: 'jhooksCgClips',
        options: [
          { label: MOUNT_FR.fenceJhooksCgClips, value: 'jhooksCgClips' },
          { label: MOUNT_FR.fenceJhooksEdgeClamps, value: 'jhooksEdgeClamps' },
          { label: MOUNT_FR.fenceEdgeClamps, value: 'edgeClamps' },
        ],
      })
    }

    // --- Spare parts ---
    dropdownInputs.push({
      label: MOUNT_FR.spareParts,
      variableName: 'sparePartsPercent',
      default: 'no',
      options: [
        { label: MOUNT_FR.spare10, value: '10' },
        { label: MOUNT_FR.spare5, value: '5' },
        { label: 'No', value: 'no' },
      ],
    })

    const FAST_RACK_MOUNTING_NAMES = ['fastRack']

    // --- Text field inputs ---
    const textFieldInputs: TextFieldInput[] = [
      {
        label: MOUNT_FR.groundSnowSs,
        variableName: 'snowSs_kPa',
        default: '',
        type: 'number',
        min: 0,
        step: 0.1,
        maxDecimals: 1,
        button: createDlubalCASnowSsButton(FAST_RACK_MOUNTING_NAMES),
      },
      {
        label: MOUNT_FR.rainOnSnowSr,
        variableName: 'snowSr_kPa',
        default: '',
        type: 'number',
        min: 0,
        step: 0.1,
        maxDecimals: 1,
        button: createDlubalCASnowSrButton(FAST_RACK_MOUNTING_NAMES),
      },
      {
        label: MOUNT_FR.windPressure,
        variableName: 'windSpeed',
        default: '',
        type: 'number',
        min: 0,
        step: 0.1,
        button: createDlubalCAWindButton(FAST_RACK_MOUNTING_NAMES),
      },
      {
        label: MOUNT_FR.cableClips,
        variableName: 'cableClipQty',
        default: 0,
        type: 'number',
        min: 0,
      },
    ]

    // --- Preferred rail lengths (multi-select; optimizes rail stock for least scrap) ---
    const checkboxGroupInputs: CheckboxGroupInput[] = [
      {
        label: MOUNT_FR.preferredRailLengths,
        variableName: 'preferredRails',
        default: FAST_RACK_PREFERRED_RAILS_DEFAULT,
        tooltip: FAST_RACK_PREFERRED_RAILS_TOOLTIP,
        labelColor: 'rgba(0, 0, 0, 0.87)',
        options: [
          { label: MOUNT_FR.rail94, value: '94' },
          { label: MOUNT_FR.rail139, value: '139' },
          { label: MOUNT_FR.rail185, value: '185' },
        ],
      },
    ]

    return {
      dropdownInputs,
      textFieldInputsAfterAnchor,
      textFieldInputs,
      checkboxGroupInputs,
      readOnlyLabelsAfterField: {
        afterVariableName: 'snowSr_kPa',
        labels: getFastRackFacetDesignSnowLabels(system, integrationData),
      },
    }
  }
}

export function getFastRackCalculatedSpanLabel(
  system: StudioSystemType,
  projectForm: FormApi
): { label: string; value: string } | undefined {
  const roofType = getRoofName(projectForm.getState().values.roof_type) as RoofTypeName | undefined
  const roofGroup = classifyRoof(roofType)
  if (roofGroup === 'unknown') return undefined
  return { label: MOUNT_FR.calculatedSpan, value: getFastRackCalculatedSpanValue(system, projectForm) }
}
